/* eslint-disable array-callback-return */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../apiServices/axios';
import {
    INFORMATION_REQUESTS,
    INFORMATION_RESPONSE,
    INFORMATION_REQUESTS_DELETE,
    BULK_INFO_REQ,
    STOCK_REQUEST,
    IRL_COMMENT,
    DEFAULT_QUESTION_BANK,
    IRLCoPilot,
} from '../apiServices/api_routes';
import { toast } from 'react-toastify';
import { capitalizeFirstLetter } from '../utils/commonUtils';
import {
    buildProjectCsvExportFilename,
    buildProjectCsvTemplateFilename,
} from '../utils/exportPdfFilename';

export const GetInformationRequests = createAsyncThunk('GET_INFORMATION_REQUESTS', async () => {
    const data = await axiosInstance.get(INFORMATION_REQUESTS + localStorage['project_id'] + '/');
    return data;
});
export const GetDefaultQuestions = createAsyncThunk('GET_DEFAULT_QUESTION_BANK', async workstream => {
    const data = await axiosInstance.get(DEFAULT_QUESTION_BANK + workstream + '/');
    return data;
});
export const PostInformationRequests = createAsyncThunk('POST_INFORMATION_REQUESTS', async ({ body }, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.post(INFORMATION_REQUESTS + localStorage['project_id'] + '/', body);
        return data;
    } catch (error) {
        return rejectWithValue(error.response.data);
    }
});
export const DeleteInformationRequests = createAsyncThunk('DELETE_INFORMATION_REQUESTS', async id => {
    const data = await axiosInstance.delete(INFORMATION_REQUESTS + localStorage['project_id'] + '/' + id + '/');
    return data;
});
export const PatchInformationRequests = createAsyncThunk('PATCH_INFORMATION_REQUESTS', async ({ body }) => {
    const data = await axiosInstance.patch(INFORMATION_REQUESTS + localStorage['project_id'] + '/' + body?.id + '/', body);
    return data;
});
export const PostInformationResponse = createAsyncThunk('POST_INFORMATION_RESPONSE', async ({ body }) => {
    const data = await axiosInstance.post(INFORMATION_RESPONSE + localStorage['project_id'] + '/', body);
    return data;
});
export const UpdateInformationResponse = createAsyncThunk('UPDATE_INFORMATION_RESPONSE', async ({ id, body }) => {
    const data = await axiosInstance.patch(INFORMATION_RESPONSE + localStorage['project_id'] + '/' + id + '/', body);
    return data;
});
export const DeleteInformationResponse = createAsyncThunk('DELETE_INFORMATION_REQUESTS_DELETE', async body => {
    const data = await axiosInstance.delete(INFORMATION_REQUESTS_DELETE + localStorage['project_id'] + '/', { data: body });
    return data;
});
export const UpdateBulkInformationRequest = createAsyncThunk('UPDATE_BULK_INFO_REQ', async body => {
    const data = await axiosInstance.put(BULK_INFO_REQ + localStorage['project_id'] + '/', body);
    return data;
});
export const UploadCSVFile = createAsyncThunk('UPLOAD_CSV_FILE', async body => {
    const data = await axiosInstance.post(INFORMATION_REQUESTS + localStorage['project_id'] + '/bulk_upload_csv/', body);
    return data;
});
export const DownloadCSVTemplate = createAsyncThunk('INFORMATION_REQUESTS_DOWNLOAD_CSV_TEMPLATE', async (_, { rejectWithValue, getState }) => {
    try {
        const response = await axiosInstance.get(INFORMATION_REQUESTS + localStorage['project_id'] + '/download_template/', {
            responseType: 'blob',
        });
        return {
            data: response.data,
            filename: buildProjectCsvTemplateFilename(getState, 'Information Requests'),
        };
    } catch (error) {
        return rejectWithValue(error.response?.data || 'Download failed');
    }
});
export const DownloadSelectedIRs = createAsyncThunk('INFORMATION_REQUESTS_DOWNLOAD_SELECTED', async (ids, { rejectWithValue, getState }) => {
    try {
        const response = await axiosInstance.post(`/dataroom/information_request/${localStorage['project_id']}/download_selected/`, { ids }, { responseType: 'blob' });
        return {
            data: response.data,
            filename: buildProjectCsvExportFilename(getState, { label: 'Information Requests', ids }),
        };
    } catch (error) {
        return rejectWithValue(error.response?.data || 'Download failed');
    }
});
export const GetStockRequest = createAsyncThunk('GET_STOCK_REQUEST', async () => {
    const data = await axiosInstance.get(STOCK_REQUEST + '/');
    return data;
});
export const AddBulkInformationRequests = createAsyncThunk('POST_INFORMATION_REQUESTS_BULK_ADD', async body => {
    const data = await axiosInstance.post(INFORMATION_REQUESTS + localStorage['project_id'] + '/bulk_add/', body);
    return data;
});
export const PostIRLComment = createAsyncThunk('POST_IRL_COMMENT', async ({ body }) => {
    const data = await axiosInstance.post(IRL_COMMENT + localStorage['project_id'] + '/', body);
    return data;
});
export const UpdateIRLComment = createAsyncThunk('UPDATE_IRL_COMMENT', async ({ id, body }) => {
    const data = await axiosInstance.patch(IRL_COMMENT + localStorage['project_id'] + '/' + id + '/', body);
    return data;
});
export const FetchIrlCopilotResults = createAsyncThunk('POST_IRLCoPilot', async ({ body, analysisType }) => {
    const data = await axiosInstance.post(IRLCoPilot + localStorage['project_id'] + '/swot-analysis/', body);
    return { data, analysisType };
});

export const GetSavedAnalysis = createAsyncThunk('GET_SAVED_ANALYSIS', async () => {
    const data = await axiosInstance.get(IRLCoPilot + localStorage['project_id'] + '/get-saved-analysis/?project_id=' + localStorage['project_id']);
    return data;
});

// AI Chat API functions
export const InitializeAIChatWithIRL = createAsyncThunk('INITIALIZE_AI_CHAT_WITH_IRL', async ({ item_ids, message }) => {
    const data = await axiosInstance.post(IRLCoPilot + localStorage['project_id'] + '/chat/initialize-with-irl/', {
        item_ids,
        message,
    });
    return data;
});

export const SendAIMessage = createAsyncThunk('SEND_AI_MESSAGE', async ({ message, item_ids }) => {
    const data = await axiosInstance.post(IRLCoPilot + localStorage['project_id'] + '/chat/send-message/', {
        message,
        item_ids,
    });
    return data;
});

export const GetAIChatMessages = createAsyncThunk('GET_AI_CHAT_MESSAGES', async () => {
    const data = await axiosInstance.get(IRLCoPilot + localStorage['project_id'] + '/chat/messages/');
    return data;
});

export const ClearAIChat = createAsyncThunk('CLEAR_AI_CHAT', async () => {
    const data = await axiosInstance.delete(IRLCoPilot + localStorage['project_id'] + '/chat/clear/');
    return data;
});

export const UpdateAIChatTitle = createAsyncThunk('UPDATE_AI_CHAT_TITLE', async ({ title }) => {
    const data = await axiosInstance.put(IRLCoPilot + localStorage['project_id'] + '/chat/title/', {
        title,
    });
    return data;
});
export const questionBankSlice = createSlice({
    name: 'questionBankSlice',
    initialState: {
        questionsData: [],
        questionsFilters: {},
        informationRequests: [],
        managementResponses: [],
        customRequestLoading: false,
        addMgmtLoading: false,
        addtoIRLBtnLoading: false,
        informationRequestsFilters: {},
        stockRequests: [],
        bulkDeleteBtnLoading: false,
        irlCommentButtonLoading: false,
        joyRideStepIndex: 0,
        questionBank: [],
        uploadCSVBtnLoading: false,
        irlCopilotLoading: false,
        downloadTemplateLoading: false,
        downloadSelectedLoading: false,
        // AI Analysis responses storage - keyed by selectedRows
        irlAiResponses: {},
        // AI Chat state
        aiChat: null,
        aiChatMessages: [],
        aiChatLoading: false,
        aiChatError: null,
    },
    reducers: {
        updateFilters: (state, { payload }) => {
            return { ...state, questionsFilters: payload };
        },
        setJoyRideStepIndex: (state, { payload }) => {
            return { ...state, joyRideStepIndex: payload };
        },
        // AI Analysis state management
        setIrlAiResponse: (state, { payload }) => {
            const { selectedRowsKey, analysisType, response } = payload;
            if (!state.irlAiResponses[selectedRowsKey]) {
                state.irlAiResponses[selectedRowsKey] = {};
            }
            state.irlAiResponses[selectedRowsKey][analysisType] = response;
        },
        clearIrlAiResponses: (state, { payload }) => {
            const { selectedRowsKey } = payload;
            if (selectedRowsKey) {
                delete state.irlAiResponses[selectedRowsKey];
            } else {
                // Clear all if no specific key provided
                state.irlAiResponses = {};
            }
        },
        // Load saved analysis from database
        loadSavedAnalysis: (state, { payload }) => {
            const { analyses } = payload;
            // Convert database format to Redux format
            state.irlAiResponses = {};
            Object.keys(analyses).forEach(analysisType => {
                const analysis = analyses[analysisType];
                // Since we don't store selected_irl_ids, use default key
                const selectedRowsKey = 'saved_analysis';
                if (!state.irlAiResponses[selectedRowsKey]) {
                    state.irlAiResponses[selectedRowsKey] = {};
                }
                state.irlAiResponses[selectedRowsKey][analysisType] = analysis.result;
            });
        },
        resetQestionBankData: (state, { payload }) => {
            return {
                questionsData: [],
                questionsFilters: {},
                informationRequests: [],
                managementResponses: [],
                customRequestLoading: false,
                addMgmtLoading: false,
                addtoIRLBtnLoading: false,
                informationRequestsFilters: {},
                stockRequests: [],
                bulkDeleteBtnLoading: false,
                irlCommentButtonLoading: false,
                joyRideStepIndex: 0,
                questionBank: [],
                uploadCSVBtnLoading: false,
                irlCopilotLoading: false,
                downloadTemplateLoading: false,
                downloadSelectedLoading: false,
                irlAiResponses: {}, // Reset AI responses when project changes
                // AI Chat state
                aiChat: null,
                aiChatMessages: [],
                aiChatLoading: false,
                aiChatError: null,
            };
        },
        // Add temporary message to chat (for immediate display)
        addTempMessage: (state, { payload }) => {
            if (state.aiChatMessages) {
                state.aiChatMessages.push(payload);
            }
        },
        // Remove temporary message from chat (on error)
        removeTempMessage: (state, { payload }) => {
            if (state.aiChatMessages) {
                state.aiChatMessages = state.aiChatMessages.filter(msg => msg.id !== payload);
            }
        },
        // Remove last AI response (for regeneration)
        removeLastAIResponse: state => {
            if (state.aiChatMessages && state.aiChatMessages.length > 0) {
                console.log('Before removing last AI response:', state.aiChatMessages.length, 'messages');
                // Find and remove the last assistant message
                for (let i = state.aiChatMessages.length - 1; i >= 0; i--) {
                    if (state.aiChatMessages[i].role === 'assistant') {
                        console.log('Removing AI response with ID:', state.aiChatMessages[i].id);
                        state.aiChatMessages.splice(i, 1);
                        break;
                    }
                }
                console.log('After removing last AI response:', state.aiChatMessages.length, 'messages');
            }
        },
    },
    extraReducers: {
        //GetInformationRequests
        [GetInformationRequests.pending]: (state, action) => {
            state.status = 'loading';
        },
        [GetInformationRequests.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            state.informationRequests = payload?.data['information requests'];
            state.informationRequestsFilters = payload?.data['filters'];
        },
        [GetInformationRequests.rejected]: (state, action) => {
            state.status = 'rejected';
            toast.error('Error');
        },
        //GetSavedAnalysis
        [GetSavedAnalysis.pending]: (state, action) => {
            state.status = 'loading';
        },
        [GetSavedAnalysis.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            // Load saved analysis into Redux state
            if (payload?.data?.analyses) {
                state.irlAiResponses = {};
                const selectedRowsKey = 'saved_analysis';
                if (!state.irlAiResponses[selectedRowsKey]) {
                    state.irlAiResponses[selectedRowsKey] = {};
                }

                // The analyses object now contains the actual analysis data directly
                Object.keys(payload.data.analyses).forEach(analysisType => {
                    state.irlAiResponses[selectedRowsKey][analysisType] = payload.data.analyses[analysisType];
                });
            }
        },
        [GetSavedAnalysis.rejected]: (state, action) => {
            state.status = 'rejected';
            console.error('Failed to load saved analysis:', action.error);
        },
        //PostInformationRequests
        [PostInformationRequests.pending]: (state, action) => {
            state.status = 'loading';
            state.customRequestLoading = true;
        },
        [PostInformationRequests.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            toast.success('Success');
            state.informationRequests.push(payload?.data);
            state.customRequestLoading = false;
        },
        [PostInformationRequests.rejected]: (state, { payload }) => {
            state.customRequestLoading = false;
            try {
                Object.keys(payload).map(key => {
                    payload[key].map(msg => toast.error(capitalizeFirstLetter(key) + ' : ' + msg));
                });
            } catch (error) {
                console.log(error);
            }
            state.status = 'rejected';
        },
        //PatchInformationRequests
        [PatchInformationRequests.pending]: (state, action) => {
            state.status = 'loading';
            state.customRequestLoading = true;
        },
        [PatchInformationRequests.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            toast.success('Success');
            state.informationRequests = state.informationRequests?.map(item => {
                if (item?.id === payload?.data?.id) return payload?.data;
                else return item;
            });
            state.customRequestLoading = false;
        },

        [PatchInformationRequests.rejected]: (state, action) => {
            state.status = 'rejected';
            toast.error('Error');
            state.customRequestLoading = false;
        },
        // DeleteInformationRequests
        [DeleteInformationRequests.fulfilled]: (state, { meta }) => {
            state.status = 'success';
            toast.success('Success');
            state.informationRequests = state.informationRequests?.filter(item => item?.id !== meta?.arg);
            // state.informationRequests = state.informationRequests?.map(item => {
            //     if (item?.id === payload?.data?.id) return payload?.data;
            //     else return item;
            // });
            state.customRequestLoading = false;
        },
        //PostInformationResponse
        [PostInformationResponse.pending]: (state, action) => {
            state.status = 'loading';
        },
        [PostInformationResponse.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            toast.success('Success');
            console.log(payload);
            state.informationRequests = state.informationRequests?.map(item => {
                if (payload?.data?.information_request === item?.id) {
                    return { ...item, irresponse: payload?.data };
                } else return item;
            });
        },
        [PostInformationResponse.rejected]: (state, action) => {
            state.status = 'rejected';
            toast.error('Error');
        },
        //UpdateInformationResponse
        [UpdateInformationResponse.pending]: (state, action) => {
            state.status = 'loading';
        },
        [UpdateInformationResponse.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            toast.success('Success');
            console.log('UpdateInformationResponse payload:', payload);
            state.informationRequests = state.informationRequests?.map(item => {
                if (item?.id === payload?.data?.information_request) {
                    return { ...item, irresponse: payload?.data };
                } else return item;
            });
        },
        [UpdateInformationResponse.rejected]: (state, action) => {
            state.status = 'rejected';
            toast.error('Error');
        },

        //DeleteInformationResponse
        [DeleteInformationResponse.pending]: (state, action) => {
            state.status = 'loading';
            state.bulkDeleteBtnLoading = true;
        },
        [DeleteInformationResponse.fulfilled]: (state, action) => {
            const ids = action?.meta?.arg?.ids;
            state.informationRequests = state.informationRequests.filter(item => {
                let check = ids.find(id => id === item?.id);
                if (!check) return item;
            });
            state.status = 'rejected';
            state.bulkDeleteBtnLoading = false;
            toast.success('Success');
        },
        [DeleteInformationResponse.rejected]: (state, action) => {
            state.status = 'rejected';
            toast.error('Error');
            state.bulkDeleteBtnLoading = false;
        },

        //UpdateBulkInformationRequest
        [UpdateBulkInformationRequest.pending]: (state, action) => {
            state.status = 'loading';
            state.addtoIRLBtnLoading = true;
        },
        [UpdateBulkInformationRequest.fulfilled]: (state, action) => {
            const ids = action?.meta?.arg?.ids;
            console.log({ ids, action });
            state.informationRequests = state.informationRequests.map(item => {
                let updated = action?.payload?.data?.find(ir => ir?.id === item?.id);
                if (updated) return updated;
                else return item;
            });
            state.status = 'success';
            toast.success('Success');
            state.addtoIRLBtnLoading = false;
        },
        [UpdateBulkInformationRequest.rejected]: (state, action) => {
            state.status = 'rejected';
            toast.error('Error');
            state.addtoIRLBtnLoading = false;
        },
        //GetStockRequest
        [GetStockRequest.pending]: (state, action) => {
            state.status = 'loading';
        },
        [GetStockRequest.fulfilled]: (state, action) => {
            state.status = 'success';
            state.stockRequests = action?.payload.data;
        },
        [GetStockRequest.rejected]: (state, action) => {
            state.status = 'rejected';
        },
        //AddBulkInformationRequests
        [AddBulkInformationRequests.pending]: (state, action) => {
            state.addtoIRLBtnLoading = true;
        },
        [AddBulkInformationRequests.fulfilled]: (state, action) => {
            toast.success('Success');
            state.addtoIRLBtnLoading = false;
            state.informationRequests = [...state.informationRequests, ...action.payload.data];
            // state.stockRequests = action?.payload.data;
        },
        [AddBulkInformationRequests.rejected]: (state, action) => {
            state.addtoIRLBtnLoading = false;
            toast.error('Rejected');
        },
        //PostIRLComment
        [PostIRLComment.pending]: (state, action) => {
            state.status = 'loading';
            state.irlCommentButtonLoading = true;
        },
        [PostIRLComment.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            toast.success('Success');
            state.informationRequests = state.informationRequests?.map(item => {
                if (item?.id === payload?.data?.information_request) {
                    return {
                        ...item,
                        irlcomment: item?.irlcomment?.length > 0 ? [...item?.irlcomment, payload?.data] : [payload?.data],
                    };
                } else return item;
            });
            state.irlComments = state?.irlComments?.length > 0 ? [...state.irlComments, payload?.data] : [payload?.data];
            state.irlCommentButtonLoading = false;
        },
        [PostIRLComment.rejected]: (state, action) => {
            toast.error('Failed');
            state.status = 'rejected';
            state.irlCommentButtonLoading = false;
        },
        //UpdateIRLComment
        [UpdateIRLComment.pending]: (state, action) => {
            state.status = 'loading';
            state.irlCommentButtonLoading = true;
        },
        [UpdateIRLComment.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            toast.success('Success');
            console.log('UpdateIRLComment payload:', payload);
            state.informationRequests = state.informationRequests?.map(item => {
                if (item?.id === payload?.data?.information_request) {
                    return {
                        ...item,
                        irlcomment: item?.irlcomment?.map(comment => {
                            if (comment?.id === payload?.data?.id) {
                                return payload?.data;
                            }
                            return comment;
                        }),
                    };
                } else return item;
            });
            state.irlCommentButtonLoading = false;
        },
        [UpdateIRLComment.rejected]: (state, action) => {
            state.status = 'rejected';
            toast.error('Error');
            state.irlCommentButtonLoading = false;
        },
        //GetDefaultQuestions
        [GetDefaultQuestions.fulfilled]: (state, action) => {
            state.questionBank = action?.payload?.data;
        },
        //UploadCSVFile
        [UploadCSVFile.pending]: (state, action) => {
            // state.questionBank = action?.payload?.data;
            state.uploadCSVBtnLoading = true;
        },
        [UploadCSVFile.fulfilled]: (state, action) => {
            // state.questionBank = action?.payload?.data;
            console.log({ action });
            state.uploadCSVBtnLoading = false;
            toast.success('CSV file uploaded');
        },
        [UploadCSVFile.rejected]: (state, action) => {
            // state.questionBank = action?.payload?.data;
            state.uploadCSVBtnLoading = false;
            toast.error(action.payload || 'Upload failed');
        },
        //FetchIrlCopilotResults
        [FetchIrlCopilotResults.pending]: (state, action) => {
            // state.questionBank = action?.payload?.data;
            state.irlCopilotLoading = true;
        },
        [FetchIrlCopilotResults.fulfilled]: (state, action) => {
            state.irlCopilotLoading = false;

            // Update Redux state with the new analysis result
            if (action.payload?.data?.data?.swot_analysis) {
                // Since we don't store selected_irl_ids, use default key
                const selectedRowsKey = 'saved_analysis';
                if (!state.irlAiResponses[selectedRowsKey]) {
                    state.irlAiResponses[selectedRowsKey] = {};
                }

                // Get analysis type from the action payload
                const analysisType = action.payload.analysisType || 'swot';
                state.irlAiResponses[selectedRowsKey][analysisType] = action.payload?.data.data.swot_analysis;
            }
        },
        [FetchIrlCopilotResults.rejected]: (state, action) => {
            // state.questionBank = action?.payload?.data;
            state.irlCopilotLoading = false;
        },
        [DownloadCSVTemplate.pending]: state => {
            state.downloadTemplateLoading = true;
        },
        [DownloadCSVTemplate.fulfilled]: (state, action) => {
            state.downloadTemplateLoading = false;
            const url = window.URL.createObjectURL(new Blob([action.payload.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', action.payload.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Template downloaded successfully');
        },
        [DownloadCSVTemplate.rejected]: (state, action) => {
            state.downloadTemplateLoading = false;
            toast.error(action.payload || 'Download failed');
        },
        [DownloadSelectedIRs.pending]: state => {
            state.downloadSelectedLoading = true;
        },
        [DownloadSelectedIRs.fulfilled]: (state, action) => {
            state.downloadSelectedLoading = false;
            const url = window.URL.createObjectURL(new Blob([action.payload.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', action.payload.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Selected items downloaded successfully');
        },
        [DownloadSelectedIRs.rejected]: (state, action) => {
            state.downloadSelectedLoading = false;
            toast.error(action.payload || 'Download failed');
        },
        // AI Chat extra reducers
        [InitializeAIChatWithIRL.pending]: (state, action) => {
            state.aiChatLoading = true;
            state.aiChatError = null;
        },
        [InitializeAIChatWithIRL.fulfilled]: (state, { payload }) => {
            state.aiChatLoading = false;
            state.aiChat = payload?.data?.chat;

            // Replace temporary user message with real one from API
            if (payload?.data?.user_message) {
                // Find and remove the temporary user message
                const tempMessageIndex = state.aiChatMessages.findIndex(msg => msg.is_temp && msg.role === 'user');
                if (tempMessageIndex !== -1) {
                    state.aiChatMessages.splice(tempMessageIndex, 1);
                }
            }

            // Add all messages from API (system, user, ai)
            if (payload?.data?.system_message) {
                state.aiChatMessages.push(payload.data.system_message);
            }
            if (payload?.data?.user_message) {
                state.aiChatMessages.push(payload.data.user_message);
            }
            if (payload?.data?.ai_response) {
                state.aiChatMessages.push(payload.data.ai_response);
            }
        },
        [InitializeAIChatWithIRL.rejected]: (state, action) => {
            state.aiChatLoading = false;

            // Check if the error is "Chat session already exists"
            if (action.error?.message && action.error.message.includes('Chat session already exists')) {
                // Don't set error for this case, just clear it
                state.aiChatError = null;
                // The chat will be loaded when the user sends the next message
            } else {
                state.aiChatError = action.error?.message || 'Failed to initialize chat';
            }
        },
        [SendAIMessage.pending]: (state, action) => {
            state.aiChatLoading = true;
            state.aiChatError = null;
        },
        [SendAIMessage.fulfilled]: (state, { payload }) => {
            state.aiChatLoading = false;

            // Update aiChat state if chat_id is provided
            if (payload?.data?.chat_id && !state.aiChat) {
                // Create a minimal chat object if we don't have one
                state.aiChat = {
                    id: payload.data.chat_id,
                    title: 'AI Chat',
                    is_active: true,
                };
            }

            // Replace temporary user message with real one from API
            if (payload?.data?.user_message) {
                // Find and remove the temporary user message
                const tempMessageIndex = state.aiChatMessages.findIndex(msg => msg.is_temp && msg.role === 'user');
                if (tempMessageIndex !== -1) {
                    state.aiChatMessages.splice(tempMessageIndex, 1);
                }
                // Add the real user message from API
                state.aiChatMessages.push(payload.data.user_message);
            }

            // Add AI response to chat history
            if (payload?.data?.ai_response) {
                state.aiChatMessages.push(payload.data.ai_response);
            }
        },
        [SendAIMessage.rejected]: (state, action) => {
            state.aiChatLoading = false;
            state.aiChatError = action.error?.message || 'Failed to send message';
        },
        [GetAIChatMessages.pending]: (state, action) => {
            state.aiChatLoading = true;
            state.aiChatError = null;
        },
        [GetAIChatMessages.fulfilled]: (state, { payload }) => {
            state.aiChatLoading = false;
            state.aiChatMessages = payload?.data?.messages || [];
        },
        [GetAIChatMessages.rejected]: (state, action) => {
            state.aiChatLoading = false;
            // Don't set error for 404 - this is expected when no chat exists
            // Just set messages to empty and continue
            state.aiChatMessages = [];
            state.aiChatError = null;
        },
        [ClearAIChat.pending]: (state, action) => {
            state.aiChatLoading = true;
        },
        [ClearAIChat.fulfilled]: (state, action) => {
            state.aiChatLoading = false;
            // Clear both chat session and messages to force re-initialization
            state.aiChat = null;
            state.aiChatMessages = [];
            state.aiChatError = null;
        },
        [ClearAIChat.rejected]: (state, action) => {
            state.aiChatLoading = false;
            state.aiChatError = action.error?.message || 'Failed to clear chat';
        },
        [UpdateAIChatTitle.pending]: (state, action) => {
            state.aiChatLoading = true;
        },
        [UpdateAIChatTitle.fulfilled]: (state, { payload }) => {
            state.aiChatLoading = false;
            if (state.aiChat) {
                state.aiChat.title = payload?.data?.title;
            }
        },
        [UpdateAIChatTitle.rejected]: (state, action) => {
            state.aiChatLoading = false;
            state.aiChatError = action.error?.message || 'Failed to update title';
        },
    },
});
export const {
    updateFilters,
    setJoyRideStepIndex,
    resetQestionBankData,
    setIrlAiResponse,
    clearIrlAiResponses,
    loadSavedAnalysis,
    addTempMessage,
    removeTempMessage,
    removeLastAIResponse,
} = questionBankSlice.actions;
export default questionBankSlice.reducer;
